import { useDebouncedValue } from '@mantine/hooks'
import { Button, ButtonWithLoader } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { useIsMutating } from '@tanstack/react-query'
import { type Edge, type Node, useReactFlow } from '@xyflow/react'
import React, { useEffect, useMemo } from 'react'
import { ASSISTANT_PANEL_WIDTH } from 'v2/pages/assistant/assistant-panel'
import { useVibeAgentHidden } from 'v2/pages/flow/components/vibe-agent/components/use-vibe-agent-gate'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { useAppStore } from 'v2/state/app'
import type { FlowTypes } from 'v2/types'
import { agentLogger } from 'v2/utils/logger'
import { sidebarComponentMap } from '../config/sidebar.config'
// import { ChatPreview } from '../components/chat-previewer'
import { showSidebar, sidebarTitle } from '../config/start-node-config'
import { useStartNodeType } from '../hooks/use-start-node-type'
import { RunType, useFlowRunStore } from '../run-info/store'
import { useFlowStore } from '../store'
import {
  type EdgeData,
  type FlowNodeTypes,
  type NodeDataBase,
  SYSTEM_PROMPT_NODE_ID,
} from '../types'
import { getNodeVarsFromAncestorEdges } from '../utils'
import { retiredNodeReason } from '../utils/retired-nodes'
import { subAccountLockedNodeReason } from '../utils/sub-account-locks'
import { CopyAgentTrigger } from './copy-agent-trigger'
import { FLOW_PANEL_SECTION_TYPOGRAPHY } from './panel-typography'
import { SystemPromptSidebar } from './system-prompt/system-prompt-sidebar'

// Stands in for a locked node's form, which would read routes the session is refused.
const LockedNodeNotice = ({ reason, testId }: { reason: string; testId: string }) => {
  // No form mounts here, so drop the previous node's: the unsaved-changes check reads it.
  useEffect(() => {
    useFlowStore.getState().setSidebarFormInstance(null)
  }, [])
  return (
    <p className="text-s-400 text-secondary" data-testid={testId}>
      {reason}
    </p>
  )
}

export const SideBar = React.memo(() => {
  const selectedNodeID = useFlowStore((state) => state.selectedNodeID)
  const unselectNode = useFlowStore((state) => state.unselectNode)
  const isAssistantOpen = useAppStore((state) => state.isAssistantOpen)
  const showVibeSidebar = useFlowStore((state) => state.showVibeSidebar)
  const vibeSidebarWidth = useFlowStore((state) => state.vibeSidebarWidth)
  // Same gate the vibe sidebar itself uses, so this offset can't disagree with
  // whether the panel is actually on screen.
  const vibeAgentHidden = useVibeAgentHidden()
  const isVibeVisible = showVibeSidebar && !vibeAgentHidden

  const isEditing = useFlowStore((state) => state.isEditing)
  // the save awaits a detection round trip, so a second click cannot start another pass
  const detectingNodeId = useFlowStore((state) => state.collectionDetection.running)
  const isDetectingCollections = !!detectingNodeId && detectingNodeId === selectedNodeID
  const runType = useFlowRunStore((state) => state.runType)
  const isGeneratingMetadata =
    useIsMutating({
      mutationKey: flowQueryKeys().generateNodeMetadata,
      predicate: (mutation) =>
        (mutation.state.variables as { nodeId?: string })?.nodeId === selectedNodeID,
    }) > 0
  const { getNode, getEdges } = useReactFlow()
  const type = useStartNodeType()
  const selectedNode = useMemo(
    () => getNode(selectedNodeID || '') as unknown as Node<NodeDataBase<unknown>> | undefined,
    [getNode, selectedNodeID],
  )

  // Get node vars for the selected node by traversing the edges up to the start node
  // Feature: Nodevars
  const [debouncedType] = useDebouncedValue(type, 500)

  const nodeVars = useMemo(() => {
    const edges = getEdges() as Edge<EdgeData>[]
    const vars = getNodeVarsFromAncestorEdges(selectedNodeID || '', edges)
    agentLogger.debug(`sidebar nodeVars for ${debouncedType}`, vars)
    return vars
  }, [getEdges, selectedNodeID, debouncedType])

  const SidebarComponent = sidebarComponentMap[
    selectedNode?.type as FlowNodeTypes
  ] as React.ElementType

  // Check if system prompt sidebar should be shown
  const showSystemPromptSidebar = selectedNodeID === SYSTEM_PROMPT_NODE_ID

  const retiredReason = retiredNodeReason(selectedNode?.type)
  if (!SidebarComponent && !showSystemPromptSidebar && !retiredReason) return null

  const lockedReason = retiredReason ?? subAccountLockedNodeReason(selectedNode?.type)
  const { icon, title } = sidebarTitle(type as FlowTypes, selectedNode)
  const panel = showSystemPromptSidebar ? (
    <SystemPromptSidebar />
  ) : showSidebar(type as FlowTypes, selectedNode?.type as FlowNodeTypes) ? (
    <div className={cn('w-[500px] max-w-full min-h-0 flex flex-col duration-300 ease-in-out py-2')}>
      <div className="pb-4 font-sora text-h4-500 text-foreground px-4">
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            {icon}
            <span className="min-w-0 break-words">{title}</span>
          </div>
          <CopyAgentTrigger selectedNodeType={selectedNode?.type} />
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-grow" scrollHideDelay={10}>
        <div className={cn('px-4', FLOW_PANEL_SECTION_TYPOGRAPHY)}>
          {selectedNode &&
            (lockedReason ? (
              <LockedNodeNotice
                reason={lockedReason}
                testId={
                  retiredReason ? 'text-sidebar-retired-node' : 'text-sidebar-subaccount-locked'
                }
              />
            ) : (
              <SidebarComponent
                key={selectedNode.id}
                selectedNode={selectedNode}
                unselectNode={unselectNode}
                nodeVars={nodeVars}
              />
            ))}
        </div>
      </ScrollArea>

      <div className="mt-auto flex justify-between px-4 mb-2 pt-2">
        <Button
          variant="outline"
          onClick={() => unselectNode()}
          tabIndex={selectedNode ? 0 : -1}
          data-testid="button-sidebar-cancel"
        >
          {runType === RunType.FlowRunLogs ? 'Close' : 'Cancel'}
        </Button>
        {runType !== RunType.FlowRunLogs && (
          <ButtonWithLoader
            form={selectedNode?.type}
            type="submit"
            tabIndex={selectedNode ? 0 : -1}
            disabled={!isEditing || !!lockedReason}
            disabledReason={
              lockedReason ?? 'Enable edit mode to make changes to node configuration'
            }
            loading={isGeneratingMetadata || isDetectingCollections}
            data-testid="button-sidebar-save"
          >
            Save
          </ButtonWithLoader>
        )}
      </div>
    </div>
  ) : null

  return (
    <div
      data-testid="sidebar"
      style={{
        right: `${(isAssistantOpen ? ASSISTANT_PANEL_WIDTH + 16 : 16) + (isVibeVisible ? vibeSidebarWidth : 0)}px`,
      }}
      className={cn(
        'fixed flex flex-row max-w-[calc(100vw-2rem)] shadow-none border-border bg-card font-inter text-foreground border bottom-2 top-[170px] overflow-hidden duration-300 ease-in-out rounded',
        selectedNodeID ? 'translate-x-0' : 'translate-x-[calc(100%+8px)]',
        !isEditing && 'editing-sidebar',
      )}
    >
      {panel}
      {/* this is used in customer feedback component so commenting for now */}
      {/* {type === 'incoming_chat' && (
        <div className="w-[600px] flex flex-col">
          <div className=" overflow-hidden">
            <ChatPreview />
          </div>
        </div>
      )} */}
    </div>
  )
})

SideBar.displayName = 'SideBar'
